import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Grid, TextField, MenuItem, Chip, LinearProgress, Button } from '@mui/material';
import { Save } from '@mui/icons-material';
import apiClient from '../utils/axiosConfig';

// Helper functions extracted to reduce cognitive complexity
const createFieldsToSave = (localValues) => [
  { field: 'credentials', value: localValues.credentials },
  { field: 'remarks', value: localValues.remarks }
];

const getFieldMap = () => ({
  'credentials': 'credentials',
  'remarks': 'remarks'
});

const saveFieldToDatabase = async (accountId, encodedManufacturer, field, value) => {
  const fieldMap = getFieldMap();
  const response = await apiClient.put(`/api/onboarding-accounts/${accountId}/manufacturers/${encodedManufacturer}/data`, {
    field: fieldMap[field],
    value: value
  });
  
  return response.data;
};

// Text input component to reduce complexity
const TextInputField = ({ label, value, type, isEvokeField }) => (
  <Grid
    size={{
      xs: 12,
      md: 4
    }}>
    <TextField
      fullWidth
      label={
        isEvokeField ? (
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {label}
            <Chip 
              label="Evoke" 
              size="small" 
              color="primary" 
              variant="outlined"
              sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
            />
          </Box>
        ) : label
      }
      type={type}
      value={value}
      size="small"
      disabled
      slotProps={{
        input: {
          readOnly: true,
          style: { color: 'black' }
        },

        inputLabel: { shrink: true, style: { color: 'black' } }
      }} />
  </Grid>
);

TextInputField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  type: PropTypes.string
};

// Dropdown field component to reduce complexity
const DropdownField = ({ step, account, manufacturer }) => {
  const isEvokeField = step.key === 'credentialsProvided' || step.key === 'hl7Form';
  
  return (
    <Grid
      size={{
        xs: 12,
        md: 4
      }}>
      <TextField
        fullWidth
        label={
          isEvokeField ? (
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {step.label}
              <Chip 
                label="Evoke" 
                size="small" 
                color="primary" 
                variant="outlined"
                sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
              />
            </Box>
          ) : step.label
        }
        value={account.manufacturerData[manufacturer]?.[step.key] || ''}
        size="small"
        disabled
        slotProps={{
          input: {
            readOnly: true,
            style: { color: 'black' }
          },

          inputLabel: { shrink: true, style: { color: 'black' } }
        }} />
    </Grid>
  );
};

DropdownField.propTypes = {
  step: PropTypes.object.isRequired,
  account: PropTypes.object.isRequired,
  manufacturer: PropTypes.string.isRequired
};

// Remarks field component
const RemarksField = ({ value }) => (
  <Grid size={12}>
    <TextField
      fullWidth
      label="Remarks"
      multiline
      rows={2}
      value={value}
      size="small"
      disabled
      slotProps={{
        input: {
          readOnly: true,
          style: { color: 'black' }
        },

        inputLabel: { shrink: true, style: { color: 'black' } }
      }} />
  </Grid>
);

RemarksField.propTypes = {
  value: PropTypes.string.isRequired
};

const ManufacturerSection = React.memo(({ account, manufacturer, calculateProgress, getStepsForManufacturer }) => {
  const manufacturerData = account.manufacturerData[manufacturer] || {};
  
  const filteredSteps = getStepsForManufacturer(manufacturer).filter(step => 
    step.key !== 'credentials' && step.key !== 'goLiveDate'
  );
  
  return (
    <Box key={manufacturer} sx={{ mb: 4, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="h6" color="primary" gutterBottom>
        {manufacturer} - {Math.round(calculateProgress(account, manufacturer))}% Complete
      </Typography>
      <LinearProgress 
        variant="determinate" 
        value={calculateProgress(account, manufacturer)} 
        sx={{ mb: 2, height: 6, borderRadius: 3 }}
      />
      <Grid container spacing={2}>
        <TextInputField 
          label="1Password User Name" 
          value={manufacturerData.credentials || ''}
          isEvokeField={true}
        />
        {filteredSteps.map((step) => (
          <DropdownField 
            key={step.key}
            step={step}
            account={account}
            manufacturer={manufacturer}
          />
        ))}
        <RemarksField 
          value={manufacturerData.remarks || ''}
        />
      </Grid>
    </Box>
  );
});

ManufacturerSection.propTypes = {
  account: PropTypes.object.isRequired,
  manufacturer: PropTypes.string.isRequired,
  calculateProgress: PropTypes.func.isRequired,
  getStepsForManufacturer: PropTypes.func.isRequired
};

export default ManufacturerSection;